<template>
  <div class="org-profile">
    <div style="height: 100%" v-if="loading">
      <loader size="xxs" :loader-image="false" />
    </div>
    <div v-else class="org-profile_inner">
      <!-- Header -->
      <div class="org-profile_header gap-1">
        <h2 class="org-profile_orgTitle">
          {{ organizationInfo.org.name || "Organization" }}
        </h2>

        <button
          v-if="!edit"
          type="button"
          class="org-profile_editLink"
          @click="handleClick"
        >
          <icon icon-name="icon-edit" size="xs" class="inline-block" />
          <span class="pl-2">Edit</span>
        </button>
      </div>

      <div class="">
        <span class="my-1 flex items-center">
          <p class="text-base font-bold capitalize text-darkPurple">
            Domain Name :
          </p>
          <p class="py-1 px-3 -mt-3 ml-3 w-max rounded-md dom-label">
            <ValidationObserver rules="required">
              <c-text
                placeholder="--Enter--"
                variant="w-full"
                :disabled="!edit"
                :errors="domainError"
                @input="domainCheck($event)"
                v-model="organizationInfo.org.alias"
              />
            </ValidationObserver>
          </p>
        </span>
      </div>

      <!-- Logo -->
      <div class="org-profile_avatarWrap">
        <image-upload
          @file="setPhoto($event)"
          :url="url"
          :disabled="disabled"
          :photo="organizationInfo.org.logoUrl"
          :name="$OrgData.name"
          image-style="height:60px; width:60px; border-radius: 8px;"
          icon-style="right: 0; bottom: 0; border: 0px !important; padding: 2px;"
        />
      </div>

      <!-- Organization details -->
      <section class="org-profile_section">
        <div class="org-profile_grid">
          <div class="org-profile_cell">
            <c-text
              placeholder="--Enter--"
              variant="w-full h-10"
              :disabled="!edit"
              label="Organization Name"
              v-model="organizationInfo.org.name"
            />
          </div>
          <div class="org-profile_cell">
            <span class="org-profile_fieldLabel">Type of Organization</span>
            <div class="org-profile_readonlyBox capitalize">
              {{ orgTypeDisplay || "—" }}
            </div>
          </div>
          <div class="org-profile_cell">
            <c-select
              placeholder="--Enter--"
              variant="w-full h-10"
              :disabled="!edit"
              label="Country of Incorporation"
              v-model="organizationInfo.org.countryOfIncorporation"
              :options="countries"
              class="country-select"
            />
          </div>

          <div class="org-profile_cell">
            <c-text
              placeholder="--Enter--"
              label="Incorporation No"
              v-model="organizationInfo.org.rcNumber"
              variant="w-full h-10"
              :disabled="!edit"
            />
          </div>
          <div class="org-profile_cell">
            <c-text
              placeholder="--Enter--"
              variant="w-full h-10"
              :disabled="!edit"
              label="Tax Identification Number (TIN)"
              v-model="organizationInfo.org.tin"
            />
          </div>
          <div class="org-profile_cell">
            <c-select
              placeholder="--Enter--"
              variant="w-full h-10"
              :disabled="!edit"
              label="Number of Employees"
              v-model="organizationInfo.org.numberOfEmployees"
              :options="employeeCountOptions"
            />
          </div>

          <div class="org-profile_cell">
            <c-select
              placeholder="--Enter--"
              variant="w-full h-10"
              :disabled="!edit"
              label="Industry"
              :options="industries"
              v-model="organizationInfo.org.industryCategory"
            />
          </div>

        </div>
      </section>

      <div class="org-profile_divider" />

      <!-- Contact Address -->
      <section class="org-profile_section">
        <h3 class="org-profile_sectionTitle">Contact Address</h3>
        <div class="org-profile_grid org-profile_grid--mt">
          <div class="org-profile_cell">
            <c-select
              placeholder="--Enter--"
              variant="w-full h-10"
              :disabled="!edit"
              label="Country"
              v-model="organizationInfo.location.country"
              :options="countries"
            />
          </div>
          <div class="org-profile_cell">
            <c-text
              placeholder="--Enter--"
              variant="w-full h-10"
              :disabled="!edit"
              label="Address"
              v-model="organizationInfo.location.address"
            />
          </div>
          <div class="org-profile_cell">
            <c-text
              placeholder="--Enter--"
              variant="w-full h-10"
              :disabled="!edit"
              label="City"
              v-model="organizationInfo.location.city"
            />
          </div>

          <div class="org-profile_cell">
            <c-select
              v-if="organizationInfo.location.country === 'NGA'"
              placeholder="--Enter--"
              variant="w-full h-10"
              :disabled="!edit"
              label="State"
              v-model="organizationInfo.location.stateId"
              :options="states"
              @onChange="onLocationStateChange"
            />
            <c-text
              v-else
              placeholder="--Enter--"
              label="State"
              :disabled="!edit"
              variant="w-full h-10"
              v-model="organizationInfo.location.state"
            />
          </div>
          <div class="org-profile_cell">
            <c-text
              placeholder="--Enter--"
              variant="w-full h-10"
              :disabled="!edit"
              label="Zipcode"
              v-model="organizationInfo.location.zipcode"
            />
          </div>
          <div class="org-profile_cell">
            <div class="org-profile_fieldLabelRow">
              <span class="org-profile_fieldLabel">Email Address</span>

            </div>
            <div
              class="org-profile_readonlyBox"
              :class="{ 'org-profile_fakeInput--clickable': edit && enableEdit }"
              @click="edit && enableEdit && (ChnageEmailModal = true)"
            >
              <!-- <icon
                v-if="edit && enableEdit"
                icon-name="icon-edit"
                size="xs"
                class="text-flame org-profile_fakeInputIcon"
              /> -->
              <span class="org-profile_fakeInputText ml-2">{{
                organizationInfo.org.email || "—"
              }}</span>
            </div>
          </div>

          <div class="w-full">
            <div class="org-profile_fieldLabelRow">
              <span class="org-profile_fieldLabel">Phone Number</span>
            </div>
            <div
              class="org-profile_readonlyBox"
              :class="{ 'org-profile_fakeInput--clickable': edit && enableEdit }"
              @click="edit && enableEdit && (ChangePhoneModal = true)"
            >
              <icon
                v-if="edit && enableEdit"
                icon-name="icon-edit"
                size="xs"
                class="text-flame org-profile_fakeInputIcon"
              />
              <span class="org-profile_fakeInputText ml-2">{{
                organizationInfo.org.phoneNo || "—"
              }}</span>
            </div>
          </div>
          <div class="org-profile_cell">
            <c-text
              placeholder="--Enter--"
              variant="w-full h-10"
              :disabled="!edit"
              label="Website"
              v-model="organizationInfo.org.website"
            />
          </div>
        </div>
      </section>

      <div class="org-profile_divider" />

      <!-- Social Pages -->
      <section class="w-full">
        <h3 class="org-profile_sectionTitle">Social Pages</h3>
        <div class="org-profile_grid org-profile_grid--mt">
          <div class="org-profile_cell">
            <label class="org-profile_fieldLabel">Facebook</label>
            <div
              class="org-profile_socialInput"
              :class="{ 'org-profile_socialInput--readonly': !edit }"
            >
              <icon icon-name="icon-facebook" size="s" class="text-flame" />
              <c-text
                placeholder="https://"
                variant="w-full h-10"
                :disabled="!edit"
                v-model="organizationInfo.org.facebook"
              />
            </div>
          </div>
          <div class="org-profile_cell">
            <label class="org-profile_fieldLabel">Instagram</label>
            <div
              class="org-profile_socialInput"
              :class="{ 'org-profile_socialInput--readonly': !edit }"
            >
                <icon icon-name="icon-instagram" size="s" class="" />
              <c-text
                placeholder="https://"
                variant="w-full h-10"
                :disabled="!edit"
                v-model="organizationInfo.org.instagram"
              />
            </div>
          </div>
          <div class="org-profile_cell">
            <label class="org-profile_fieldLabel">LinkedIn</label>
            <div
              class="org-profile_socialInput"
              :class="{ 'org-profile_socialInput--readonly': !edit }"
            >
              <icon icon-name="icon-linkedin" size="s" class="text-flame" />
              <c-text
                placeholder="https://"
                variant="w-full h-10"
                :disabled="!edit"
                v-model="organizationInfo.org.linkedIn"
              />
            </div>
          </div>
          <div class="org-profile_cell">
            <label class="org-profile_fieldLabel">Twitter</label>
            <div
              class="org-profile_socialInput"
              :class="{ 'org-profile_socialInput--readonly': !edit }"
            >
              <icon icon-name="icon-twitter" size="s" class="text-flame" />
              <c-text
                placeholder="https://"
                variant="w-full h-10"
                :disabled="!edit"
                v-model="organizationInfo.org.twitter"
              />
            </div>
          </div>
        </div>
      </section>

      <div class="org-profile_divider" />

      <div class="org-profile_actions" v-if="edit">
        <Button
          class="org-profile_btnPrimary bg-dynamicBackBtn text-white"
          @click="handleSave()"
          :disabled="disableBtn"
        >
          Save changes
        </Button>
        <Button class=" buttons" @click="revertChanges">
          Revert changes
        </Button>
      </div>
    </div>
    <ChangeEmail
      :open-modal="ChnageEmailModal"
      @close="ChnageEmailModal = false"
      v-if="enableEdit"
    />
    <ChangePhone
      :open-modal="ChangePhoneModal"
      @save="editNewPhone($event)"
      @close="ChangePhoneModal = false"
      v-if="enableEdit"
    />
  </div>
</template>

<script>
import CText from "@scelloo/cloudenly-ui/src/components/text";
import { mapState, mapActions } from "vuex";
import CSelect from "@scelloo/cloudenly-ui/src/components/select";
import imageUpload from "@/components/imageUpload";
import countries from "@/helpers/countries";
import states from "@/helpers/states";
import timezones from "@/helpers/timezone";
import Button from "@/components/Button";
import Icon from "@/components/Icon";
import ChangeEmail from "../sidebar/ChangeEmail";
import ChangePhone from "../sidebar/ChangePhone";

export default {
  components: {
    imageUpload,
    CText,
    Button,
    CSelect,
    ChangeEmail,
    ChangePhone,
    Icon,
  },
  name: "Organisation",
  data() {
    return {
      disableBtn: false,
      edit: false,
      countries,
      states,
      timezones,
      loading: true,
      enableEdit: false,
      url: process.env.VUE_APP_IMAGE_UPLOAD,
      photo: "",
      response: "",
      responseBg: "",
      success: false,
      ChnageEmailModal: false,
      ChangePhoneModal: false,
      languages: ["English"],
      selectedSubIndustry: "",
      disabled: false,
      employeeCountOptions: [
        { id: "1-10", name: "1 – 10" },
        { id: "11-50", name: "11 – 50" },
        { id: "51-200", name: "51 – 200" },
        { id: "201-500", name: "201 – 500" },
        { id: "501+", name: "501+" },
      ],
      organizationInfo: {
        org: {
          id: "id",
          logoUrl: null,
          name: "",
          alias: "",
          type: "",
          orgType: "",
          tin: "",
          countryOfIncorporation: "",
          numberOfEmployees: "",
          rcNumber: "",
          email: "",
          dialCode: "234",
          phoneNo: "",
          website: "",
          industryCategory: "5",
          businessCoverage: "",
          timeZone: null,
          language: null,
          facebook: "",
          twitter: "",
          instagram: "",
          linkedIn: "",
        },
        location: {
          id: "",
          address: "",
          city: "",
          state: "",
          stateId: null,
          country: "",
          zipcode: "",
          latitude: null,
          longitude: null,
        },
        specializations: [],
      },
      orgType: [],
      domainError: [],
      domainExist: false,
      modals: {
        email: { token: "", newModal: false, otpModal: false },
        phone: { token: "", newModal: false, otpModal: false },
      },
      editModeSnapshot: null,
    };
  },
  watch: {
    selectedSubIndustry() {
      if (this.selectedSubIndustry) {
        this.addSpecialization(this.selectedSubIndustry);
      }
    },
  },
  computed: {
    ...mapState({
      industries: (state) => state.industries,
      rootUser: (state) => state.org.rootUser,
    }),
    orgTypeDisplay() {
      const org = this.organizationInfo?.org || {};
      const direct = String(org.orgType || "").trim();
      if (direct) return direct;
      const typeValue = org.type;
      if (typeValue === null || typeValue === undefined || typeValue === "") {
        return "";
      }
      const byId = (this.orgType || []).find(
        (t) => String(t.id) === String(typeValue)
      );
      if (byId?.name) return byId.name;
      return String(typeValue);
    },
    getSubIndustries() {
      const result = this.industries.find(
        (item) => item.id === this.organizationInfo.org.industryCategory
      );
      if (result) {
        return result.subIndustries;
      }
      return [];
    },
  },
  methods: {
    ...mapActions({
      updateProfile: "org/updateProfile",
      sendTwoFactorCode: "org/sendTwoFactorCode",
    }),
    deepClone(obj) {
      return JSON.parse(JSON.stringify(obj));
    },
    captureEditSnapshot() {
      this.editModeSnapshot = {
        organizationInfo: this.deepClone(this.organizationInfo),
        selectedSubIndustry: this.selectedSubIndustry,
        photo: this.photo,
      };
    },
    revertChanges() {
      if (this.editModeSnapshot) {
        this.organizationInfo = this.deepClone(
          this.editModeSnapshot.organizationInfo
        );
        this.selectedSubIndustry = this.editModeSnapshot.selectedSubIndustry;
        this.photo = this.editModeSnapshot.photo;
        this.domainError = [];
        this.domainExist = false;
        this.syncLocationStateId();
      }
      this.disableBtn = false;
      this.edit = false;
    },
    getLocationStateName(location) {
      if (!location) return "";
      const { state } = location;
      if (state && typeof state === "object") return String(state.name || "").trim();
      return String(state || "").trim();
    },
    onLocationStateChange(stateId) {
      const loc = this.organizationInfo?.location;
      if (!loc) return;
      const selected = this.states.find(
        (s) => String(s.id) === String(stateId)
      );
      if (selected) {
        loc.stateId = selected.id;
        loc.state = selected.name;
      } else {
        loc.stateId = stateId || null;
        loc.state = "";
      }
    },
    syncLocationStateId() {
      const loc = this.organizationInfo?.location;
      if (!loc || loc.country !== "NGA") return;

      const stateName = this.getLocationStateName(loc);
      const byName = stateName
        ? this.states.find(
            (s) => s.name.toLowerCase() === stateName.toLowerCase()
          )
        : null;

      if (loc.stateId != null && loc.stateId !== "") {
        const byId = this.states.find(
          (s) => String(s.id) === String(loc.stateId)
        );
        // API can return a stateId that does not match state (e.g. id 25 + "Enugu")
        if (byName && byId && String(byName.id) !== String(byId.id)) {
          loc.stateId = byName.id;
          loc.state = byName.name;
          return;
        }
        if (byId) {
          loc.stateId = byId.id;
          loc.state = byId.name;
          return;
        }
      }

      if (byName) {
        loc.stateId = byName.id;
        loc.state = byName.name;
      }
    },
    async loadOrganizationLocation() {
      await this.$store.dispatch("location/getDefaultLocation");
      this.organizationInfo.location = {
        ...this.$store.getters["location/defaultLocation"],
      };
      this.syncLocationStateId();
    },
    mergeRawOrgProfile() {
      const raw = this.$store.state.org.orgProfile || {};
      const o = this.organizationInfo.org;
      if (raw.tin != null && raw.tin !== "") o.tin = raw.tin;
      if (raw.taxIdentificationNumber != null && raw.taxIdentificationNumber !== "")
        o.tin = o.tin || raw.taxIdentificationNumber;
      if (raw.countryOfIncorporation != null && raw.countryOfIncorporation !== "")
        o.countryOfIncorporation = raw.countryOfIncorporation;
      if (raw.incorporationCountry != null && raw.incorporationCountry !== "")
        o.countryOfIncorporation = raw.incorporationCountry;
      if (raw.numberOfEmployees != null && raw.numberOfEmployees !== "")
        o.numberOfEmployees = raw.numberOfEmployees;
      if (raw.employeeCountBand != null && raw.employeeCountBand !== "")
        o.numberOfEmployees = raw.employeeCountBand;
    },
    async handleClick() {
      try {
        await this.$handlePrivilege("profile", "editAccountProfileInformation");
        const nextEdit = !this.edit;
        this.edit = nextEdit;
        if (nextEdit) {
          this.captureEditSnapshot();
        }
      } catch (error) {
        this.$toasted.error("You do not have permission to perform this task", {
          duration: 5000,
        });
      }
    },
    async permitEdit() {
      try {
        await this.$handlePrivilege("profile", "editAccountProfileInformation");
        this.enableEdit = true;
      } catch (error) {
        this.$toasted.error("You do not have permission to perform this task", {
          duration: 5000,
        });
      }
    },
    async handlePromise() {
      try {
        await this.$handlePrivilege("profile", "editAccountProfileInformation");
        this.disabled = false;
      } catch (error) {
        this.disabled = true;
      }
    },
    setPhoto(value) {
      this.photo = value;
      this.organizationInfo.org.logoUrl = value;
    },
    handleManageSub() {
      return window.location.replace(
        `${process.env.VUE_APP_CLOUDENLY}subscriptions/management`
      );
    },

    domainCheck(value) {
      const lowerCaseDomain = value.toLowerCase();
      const formatDomain = lowerCaseDomain.replaceAll(" ", "-");
      this.$_checkDomain(formatDomain).then((response) => {
        this.domainExist = response.data.aliasExists;
        if (this.domainExist) {
          this.domainError.push(`Domain name already in use`);
        } else {
          this.domainError = [];
        }
      });
    },

    handleSave() {
      if (this.domainError.length === 0) {
        this.disableBtn = true;
        if (this.organizationInfo.location.stateId) {
          const selectedState = this.states.find(
            (state) =>
              String(state.id) ===
              String(this.organizationInfo.location.stateId)
          );
          if (selectedState) {
            this.organizationInfo.location.stateId = selectedState.id;
            this.organizationInfo.location.state = selectedState.name;
          }
        }
        const promise = this.updateProfile(this.organizationInfo);

        promise
          .then(async (result) => {
            this.$toasted.success(result.message, { duration: 5000 });
            this.response = result.message;
            this.responseBg = "bg-mediumSeaGreen";
            this.editModeSnapshot = null;
            this.edit = false;
            await this.loadOrganizationLocation();
            window.scrollTo({ top: 0, behavior: "smooth" });
            this.disableBtn = false;
          })
          .catch(() => {
            this.disableBtn = false;
          });

        this.profileEditable = false;
        this.contactEditable = false;
        this.additionalEditable = false;
      } else {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    },
    addSpecialization(id) {
      const subIndustry = this.getSubIndustries.find((item) => item.id === id);
      this.organizationInfo.specializations = [
        {
          orgId: this.$orgId,
          subIndustryId: subIndustry.id,
        },
      ];
    },
    async saveNewEmail(email) {
      const oldEmail = this.organizationInfo.org.email;
      try {
        this.organizationInfo.org.email = email;
        this.$store.commit("SET_TOAST", {
          text: "Email updated successfully",
          color: "#009432",
        });
        this.$_updateOrgEmail({
          userId: this.rootUser.id,
          token: this.modals.email.token,
          email,
        });
        this.profileEditable = false;
      } catch (err) {
        this.$store.commit("SET_TOAST", {
          text: "Email update failed reverting back",
          color: "#EA2027",
        });
        this.organizationInfo.org.email = oldEmail;
        throw new Error(err);
      }
      this.profileEditable = false;
    },

    async editNewPhone(phoneData) {
      this.organizationInfo.org.phoneNo = phoneData.phoneNo;
    },

    initTwoFactorCode(email, type) {
      this.sendTwoFactorCode({ userId: this.rootUser.id, email });
      if (type === "phone") {
        this.modals.phone.otpModal = true;
      } else {
        this.modals.email.otpModal = true;
      }
    },
    initNewEmail(token, type) {
      if (type === "phone") {
        this.modals.phone.newModal = true;
        this.modals.phone.token = token;
      } else {
        this.modals.email.newModal = true;
        this.modals.email.token = token;
      }
    },
    mountOrgLogo() {
      this.$_getOrgSettings().then((result) => {
        const org = result?.data?.org || {};
        this.organizationInfo.org.logoUrl = org.logoUrl;
        if (org.tin != null && org.tin !== "")
          this.organizationInfo.org.tin = org.tin;
        if (org.taxIdentificationNumber != null && org.taxIdentificationNumber !== "")
          this.organizationInfo.org.tin =
            this.organizationInfo.org.tin || org.taxIdentificationNumber;
        if (org.countryOfIncorporation != null && org.countryOfIncorporation !== "")
          this.organizationInfo.org.countryOfIncorporation = org.countryOfIncorporation;
        if (org.incorporationCountry != null && org.incorporationCountry !== "")
          this.organizationInfo.org.countryOfIncorporation = org.incorporationCountry;
        if (org.numberOfEmployees != null && org.numberOfEmployees !== "")
          this.organizationInfo.org.numberOfEmployees = org.numberOfEmployees;
        if (org.employeeCountBand != null && org.employeeCountBand !== "")
          this.organizationInfo.org.numberOfEmployees = org.employeeCountBand;

        const selectedSubIndustry = (org.specializations || []).map((v) => ({
          id: v.id,
          name: v.name,
        }));
        if (selectedSubIndustry.length) {
          this.selectedSubIndustry = selectedSubIndustry[0].id;
        }
      });
      this.orgType = [
        { id: 1, name: "Sole Proprietorship" },
        { id: 2, name: "Private Limited Company (LTD)" },
        { id: 3, name: "Public Limited Company (PLC)" },
        { id: 4, name: "Limited by Gurantee" },
        { id: 5, name: "Unlimited Company" },
      ];
    },
  },
  async mounted() {
    await this.$store.dispatch("getIndustries");
    this.organizationInfo.org = this.$store.getters["org/orgProfile"];
    this.mergeRawOrgProfile();
    this.organizationInfo.specializations =
      this.$store.getters["org/orgSpecializations"];
    await this.loadOrganizationLocation();
    if (
      !this.organizationInfo.org.countryOfIncorporation &&
      this.organizationInfo.location.country
    ) {
      this.organizationInfo.org.countryOfIncorporation =
        this.organizationInfo.location.country;
    }
    this.loading = false;
    await this.permitEdit();
    await this.mountOrgLogo();
    await this.handlePromise();
  },
};
</script>

<style scoped>
.org-profile_inner {
  max-width: 1006px;
  margin-top: 1.5rem;
}

.org-profile_header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.5rem;
}

.org-profile_orgTitle {
  font-size: 12px;
  font-weight: 700;
  line-height: 1.35;
  color: #321c3b;
  margin: 0;
  flex: 1;
  min-width: 0;
}

.org-profile_editLink {
  background: none;
  border: none;
  cursor: pointer;
  color: #f15a29;
  font-size: 1rem;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
}

.org-profile_avatarWrap {
  margin-top: 0.75rem;
  margin-bottom: 1.5rem;
}

.org-profile_avatarWrap >>> .imgCard {
  margin-top: 0;
}

.org-profile_section {
  margin-bottom: 0.25rem;
}

.org-profile_sectionTitle {
  font-size: 0.875rem;
  font-weight: 600;
  color: #321c3b;
  margin: 0 0 0.25rem 0;
  letter-spacing: 0.02em;
}

.org-profile_grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1.5rem 1.25rem;
}

.org-profile_grid--mt {
  margin-top: 1.25rem;
}

.org-profile_cell {
  min-width: 0;
}

@media (max-width: 1024px) {
  .org-profile_grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 640px) {
  .org-profile_grid {
    grid-template-columns: 1fr;
  }
}

.org-profile_divider {
  height: 1px;
  background: rgba(0, 0, 0, 0.12);
  margin: 1.75rem 0;
}

.org-profile_fieldLabelRow {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.35rem;
}

.org-profile_fakeInput--clickable {
  cursor: pointer;
}

.org-profile_fakeInputIcon {
  flex-shrink: 0;
}

.org-profile_fakeInputText {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.org-profile_fieldLabel {
  display: block;
  font-size: 14px;
  color: #333;
  letter-spacing: 0.5px;
  margin-bottom: 0.35rem;
}

.org-profile_readonlyBox {
  display: flex;
  align-items: center;
  min-height: 40px;
  padding: 0 12px;
  border-radius: 4px;
  background: #eeeeee;
  color: #321c3b;
  font-size: 0.95rem;
}

.org-profile_socialInput {
  display: flex;
  align-items: stretch;
  gap: 0.5rem;
  border: 1px solid #878e99;
  border-radius: 4px;
  padding: 0 8px 0 10px;
  background: #fff;
}

.org-profile_socialInput--readonly {
  background: #eeeeee;
  border: none;
}

.org-profile_socialInput >>> input {
  border: none !important;
  box-shadow: none !important;
}

.org-profile_socialInput >>> .c-text {
  flex: 1;
  min-width: 0;
}

.org-profile_actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 1.125rem;
  margin-top: 0.5rem;
  margin-bottom: 2rem;
}

.org-profile_btnPrimary {
  min-width: 144px;
  height: 44px;
  border-radius: 5px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
}

.org-profile_btnSecondary {
  min-width: 126px;
  height: 44px;
  border-radius: 5px;
  font-weight: 600;
}

.buttons {
  width: 126px;
  height: 44px;
  border-radius: 5px;
}
</style>
